import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight, Phone, DollarSign, ShieldCheck, CalendarCheck, MapPin, Car,
  Percent, Building2, User, Truck, Ban, Wrench, BadgeCheck, ListChecks,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { BELO_PROVIDER } from "@/data/cityLocations";
import { EarningsCalculator } from "@/components/host/EarningsCalculator";
import api from "@/lib/api";
import { track } from "@/lib/analytics";

const Section = ({ id, className = "", children }) => (
  <section id={id} className={`py-16 px-5 sm:px-8 ${className}`}>{children}</section>
);
const H2 = ({ children }) => <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">{children}</h2>;

const STEPS = [
  { icon: ListChecks, title: "Apply in minutes", body: "Tell us about you and your vehicle, upload your license, registration, and proof of insurance." },
  { icon: DollarSign, title: "Set your price & availability", body: "You choose your daily, weekly, and monthly rates, delivery fee, mileage allowance, and calendar." },
  { icon: CalendarCheck, title: "Get booked & paid", body: "We connect you with Massachusetts renters. Eligible payouts are initiated by Stripe ACH every Friday." },
];
const HOST_TYPES = [
  { icon: User, t: "Single-vehicle owners", b: "Offset a car payment with a vehicle you're not always using." },
  { icon: Car, t: "Multi-car owners & Turo hosts", b: "Bring your fleet to a local marketplace with no host commission." },
  { icon: Building2, t: "Dealerships & fleets", b: "Put idle inventory to work with tools built to scale." },
  { icon: Truck, t: "Existing rental businesses", b: "Add a Massachusetts-focused channel alongside your current bookings." },
];
const HOST_RESOURCES = [
  { group: "Getting started", links: [
    ["/how-hosting-works", "How hosting works"],
    ["/host-requirements", "Host requirements"],
    ["/host-guides", "Host guides & how-tos"],
    ["/rent-out-your-car-massachusetts", "Rent out your car in MA"],
  ] },
  { group: "Earnings & payouts", links: [
    ["/host-earnings", "How host earnings work"],
    ["/host-payouts", "How payouts work"],
    ["/no-host-fee-car-rental-marketplace", "No host commission"],
  ] },
  { group: "Insurance & control", links: [
    ["/host-insurance", "Host insurance & responsibility"],
    ["/self-managed-car-hosting", "Self-managed hosting"],
    ["/managed-car-hosting", "Managed hosting"],
  ] },
  { group: "Compare & scale", links: [
    ["/turo-alternative-for-hosts", "Turo alternative for hosts"],
    ["/switch-from-turo-to-belo-rentals", "Switching from Turo"],
    ["/fleet-partnerships", "Fleet & dealership partnerships"],
  ] },
];
const HOST_RESOURCES_PT = [
  ["/pt/como-funciona-a-hospedagem", "Como funciona"],
  ["/pt/ganhos-do-anfitriao", "Ganhos do anfitrião"],
  ["/pt/requisitos-do-anfitriao", "Requisitos"],
  ["/pt/seguro-do-anfitriao", "Seguro"],
  ["/pt/pagamentos-ao-anfitriao", "Pagamentos"],
  ["/pt/alternativa-a-turo-para-anfitrioes", "Alternativa à Turo"],
];

export default function BecomeAHost() {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api.get("/host-program/config").then((r) => setCfg(r.data)).catch(() => setCfg(null)); }, []);

  const applyCta = (loc) => track("application_started", { page: "become_a_host", location: loc });
  const phoneCta = () => track("host_phone_click", { page: "become_a_host" });

  const faqs = cfg?.faqs || [];
  const faqLd = { "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })) };
  const orgLd = { "@context": "https://schema.org", "@type": "Service", serviceType: "Car rental host marketplace",
    name: "Become a Car Rental Host in Massachusetts", areaServed: { "@type": "State", name: "Massachusetts" }, provider: { ...BELO_PROVIDER } };
  const crumbLd = { "@context": "https://schema.org", "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://belorentals.com/" },
      { "@type": "ListItem", position: 2, name: "Become a Host", item: "https://belorentals.com/become-a-host" },
    ] };

  return (
    <div data-testid="become-a-host" className="bg-white">
      <Seo
        title="Become a Car Rental Host in Massachusetts | Belo Rentals"
        description="List your vehicle with Belo Rentals, set your own rental price, and keep your full listed rental rate. No host commission. Friday Stripe payouts."
        path="/become-a-host"
        keywords="become a car rental host Massachusetts, rent out your car MA, no host fee car rental marketplace, Turo alternative for hosts, list your car Massachusetts"
        jsonLd={[faqLd, orgLd, crumbLd]}
      />

      {/* Hero */}
      <section className="relative pt-32 pb-20 px-5 sm:px-8 bg-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(37,99,235,0.25),transparent)]" />
        <div className="relative max-w-4xl mx-auto text-center">
          <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-blue-500/15 text-blue-200 text-sm font-medium mb-6">
            <Percent size={14} /> No host commission · You keep your listed rate
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Set Your Price. Keep Your Listed Rental Rate.</h1>
          <p className="mt-6 text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            List your vehicle with a growing Massachusetts car-rental marketplace. Belo Rentals does not deduct a host commission from the rental price you set.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Link to="/host" onClick={() => applyCta("hero")} data-testid="apply-host-hero" className="px-7 py-3.5 rounded-full bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors inline-flex items-center gap-2">Apply to become a host <ChevronRight size={18} /></Link>
            <a href="#calculator" className="px-7 py-3.5 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors">Calculate potential earnings</a>
            <a href="tel:+19782211451" onClick={phoneCta} data-testid="host-phone-hero" className="px-7 py-3.5 rounded-full border border-white/25 text-white font-semibold hover:bg-white/10 transition-colors inline-flex items-center gap-2"><Phone size={16} /> Call 978-221-1451</a>
          </div>
        </div>
      </section>

      {/* How hosting works */}
      <Section className="max-w-6xl mx-auto">
        <div className="text-center max-w-2xl mx-auto"><H2>How hosting works</H2></div>
        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          {STEPS.map((s, i) => (
            <div key={i} className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <span className="grid place-items-center w-11 h-11 rounded-xl bg-slate-900 text-white mb-4"><s.icon size={20} /></span>
              <h3 className="font-heading font-bold text-slate-900">{s.title}</h3>
              <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </Section>

      {/* No commission + what hosts receive */}
      <Section className="bg-slate-50">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <H2>No host commission on your rate</H2>
            <p className="text-slate-600 mt-4 leading-relaxed">{cfg?.expanded_message}</p>
            <p className="text-slate-500 text-sm mt-4">
              Belo Rentals may charge the renter a separate service fee. That customer fee is never taken from your listed rental price. Taxes and government-imposed charges are shown and accounted for separately.
            </p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-7">
            <p className="label-caps text-blue-600 mb-4">You also receive 100% of these approved charges</p>
            <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2.5">
              {(cfg?.eligible_host_charges || []).map((c, i) => (
                <p key={i} className="flex items-center gap-2 text-slate-700 text-sm"><BadgeCheck size={16} className="text-emerald-500 shrink-0" /> {c}</p>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Self vs managed */}
      <Section className="max-w-6xl mx-auto">
        <H2>Self-managed or managed hosting</H2>
        <div className="grid md:grid-cols-2 gap-5 mt-8">
          <div data-testid="self-managed-card" className="bg-white rounded-2xl border border-slate-200 p-7">
            <h3 className="font-heading text-xl font-bold text-slate-900">Self-managed hosting</h3>
            <p className="text-slate-600 text-sm mt-2 leading-relaxed">You manage pricing, availability, guest handoff, delivery, cleaning, returns, maintenance, claims, and day-to-day vehicle operations.</p>
          </div>
          <div data-testid="managed-card" className="bg-white rounded-2xl border border-slate-200 p-7">
            <h3 className="font-heading text-xl font-bold text-slate-900">Managed hosting</h3>
            <p className="text-slate-600 text-sm mt-2 leading-relaxed">{cfg?.managed_hosting_note}</p>
            <Link to="/contact" onClick={() => track("managed_hosting_selected", { page: "become_a_host" })} data-testid="managed-inquiry" className="inline-flex items-center gap-1.5 text-blue-600 font-semibold text-sm mt-3 hover:text-blue-700">Request managed hosting info <ChevronRight size={15} /></Link>
          </div>
        </div>
      </Section>

      {/* Host types */}
      <Section className="bg-slate-50">
        <div className="max-w-6xl mx-auto">
          <H2>Who hosts with Belo Rentals</H2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-8">
            {HOST_TYPES.map((h, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 p-6">
                <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600 text-white mb-4"><h.icon size={20} /></span>
                <h3 className="font-heading font-bold text-slate-900">{h.t}</h3>
                <p className="text-slate-600 text-sm mt-1.5 leading-relaxed">{h.b}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Calculator */}
      <Section id="calculator" className="max-w-6xl mx-auto scroll-mt-24">
        <div className="text-center max-w-2xl mx-auto mb-10"><H2>Estimate your earnings</H2>
          <p className="text-slate-600 mt-3">Adjust the numbers to see your estimated gross host payout. There's no host commission deducted from your rate.</p></div>
        <EarningsCalculator />
      </Section>

      {/* Friday payouts + credibility */}
      <Section className="bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10">
          <div>
            <span className="grid place-items-center w-11 h-11 rounded-xl bg-blue-600 mb-4"><CalendarCheck size={20} /></span>
            <H2><span className="text-white">Weekly Friday payouts</span></H2>
            <p className="text-slate-300 mt-4 leading-relaxed text-sm">{cfg?.payout?.disclaimer}</p>
            <p className="text-slate-400 mt-3 text-sm">Hosts complete Stripe identity and bank-account verification before receiving payouts.</p>
          </div>
          <div className="flex flex-col justify-center">
            <p className="font-heading text-5xl font-bold">{cfg?.vehicle_network_size || 100}+</p>
            <p className="text-slate-300 mt-2">Belo Rentals provides access to a growing network of more than {cfg?.vehicle_network_size || 100} vehicles across Massachusetts — and we're expanding.</p>
          </div>
        </div>
      </Section>

      {/* Insurance responsibility */}
      <Section className="bg-amber-50 border-y border-amber-100">
        <div className="max-w-4xl mx-auto">
          <div className="flex items-center gap-2 text-amber-700 font-semibold"><ShieldCheck size={18} /> Insurance responsibility</div>
          <h2 className="font-heading text-2xl font-bold text-slate-900 mt-3">You maintain your own insurance</h2>
          <p className="text-slate-700 mt-3 leading-relaxed text-sm" data-testid="insurance-language">{cfg?.insurance_language}</p>
        </div>
      </Section>

      {/* Vehicle eligibility + prohibited uses */}
      <Section className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-10">
        <div>
          <div className="flex items-center gap-2 text-blue-600 font-semibold"><Wrench size={18} /> Vehicle eligibility</div>
          <H2><span className="block mt-2 text-3xl">What we accept</span></H2>
          <ul className="mt-5 space-y-2.5 text-sm">
            {["No maximum vehicle age", "No maximum mileage", "Financed and leased vehicles may apply", "No Belo mechanical inspection required", "No GPS tracker or kill switch required"].map((x, i) => (
              <li key={i} className="flex items-center gap-2 text-slate-700"><BadgeCheck size={16} className="text-emerald-500 shrink-0" /> {x}</li>
            ))}
            <li className="flex items-center gap-2 text-slate-700"><Ban size={16} className="text-rose-500 shrink-0" /> No salvage or rebuilt titles</li>
          </ul>
          <p className="text-slate-500 text-xs mt-4 leading-relaxed">{cfg?.vehicle_eligibility?.notes}</p>
        </div>
        <div>
          <div className="flex items-center gap-2 text-rose-600 font-semibold"><Ban size={18} /> Prohibited renter uses</div>
          <H2><span className="block mt-2 text-3xl">Strictly not allowed</span></H2>
          <p className="text-slate-600 text-sm mt-4">Vehicles may not be used for any for-hire, rideshare, or delivery activity, including:</p>
          <div className="flex flex-wrap gap-2 mt-4" data-testid="prohibited-uses">
            {(cfg?.prohibited_uses || []).map((p, i) => (
              <span key={i} className="text-xs font-medium bg-rose-50 text-rose-700 border border-rose-100 px-2.5 py-1 rounded-full">{p}</span>
            ))}
          </div>
        </div>
      </Section>

      {/* Hosting resources hub */}
      <Section className="max-w-6xl mx-auto" id="resources">
        <div className="text-center max-w-2xl mx-auto"><H2>Hosting resources</H2>
          <p className="text-slate-600 mt-3">Everything you need to know before you list — the process, earnings, requirements, insurance, and more.</p></div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mt-10" data-testid="host-resources">
          {HOST_RESOURCES.map((col) => (
            <div key={col.group} className="bg-slate-50 rounded-2xl p-6 border border-slate-100">
              <p className="label-caps text-blue-600 mb-3">{col.group}</p>
              <ul className="space-y-2.5">
                {col.links.map(([to, label]) => (
                  <li key={to}>
                    <Link to={to} data-testid={`host-resource-${to.replace(/\//g, "")}`} className="inline-flex items-center gap-1.5 text-slate-700 text-sm font-medium hover:text-blue-700 transition-colors">
                      <ChevronRight size={14} className="text-blue-500 shrink-0" /> {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
        <div className="mt-8 flex flex-wrap items-center gap-x-3 gap-y-2" data-testid="host-resources-pt">
          <span className="text-sm font-semibold text-slate-500">Em português:</span>
          {HOST_RESOURCES_PT.map(([to, label]) => (
            <Link key={to} to={to} className="px-3.5 py-1.5 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-medium hover:border-blue-400 hover:text-blue-700 transition-colors">{label}</Link>
          ))}
        </div>
      </Section>

      {/* FAQ */}
      <Section className="bg-slate-50">
        <div className="max-w-3xl mx-auto">
          <div className="text-center"><H2>Host FAQs</H2></div>
          <div className="mt-10 space-y-3">
            {faqs.map((f, i) => (
              <div key={i} data-testid={`host-faq-${i}`} className="bg-white rounded-xl border border-slate-200 p-5">
                <p className="font-semibold text-slate-900">{f.q}</p>
                <p className="text-slate-600 text-sm mt-2 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <Section>
        <div className="max-w-4xl mx-auto rounded-3xl bg-gradient-to-r from-blue-600 to-blue-700 text-white text-center px-8 py-14">
          <h2 className="font-heading text-3xl sm:text-4xl font-bold">Ready to list your vehicle?</h2>
          <p className="mt-4 text-blue-50 max-w-xl mx-auto">Set your price and keep your listed rental rate. Most complete applications are reviewed within 24 hours.</p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link to="/host" onClick={() => applyCta("footer")} data-testid="apply-host-footer" className="px-8 py-3.5 rounded-full bg-white text-blue-700 font-semibold hover:bg-blue-50 transition-colors inline-flex items-center gap-2">Apply to become a host <ChevronRight size={18} /></Link>
            <a href="tel:+19782211451" onClick={phoneCta} data-testid="host-phone-footer" className="px-8 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-colors inline-flex items-center gap-2"><Phone size={16} /> 978-221-1451</a>
          </div>
        </div>
      </Section>
    </div>
  );
}
